<template>
  <div>
    <div v-if="show" class="full-content-loader" aria-busy="true" aria-live="polite">

      <div class="container-content form-container container">
        <div class="page-header">
          <div class="d-none d-sm-block">
            <i class="sk sk-line" style="width: 33%; height: 10px" />
          </div>
          <div class="d-block d-sm-none">
            <i class="sk sk-line" style="width: 83%; height: 20px" />
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-12 col-xl-8 offset-md-2">
            <div class="sk-card">
              <div class="sk-id">
                <i class="sk sk-avatar" />
                <div class="sk-id-text">
                  <i class="sk sk-line" style="width: 34%; height: 15px" />
                  <i class="sk sk-line" style="width: 17%; height: 10px; margin-top: 9px" />
                </div>
              </div>

              <i class="sk sk-line" style="width: 30%; height: 10px; margin-top: 25px" />
              <i class="sk sk-field" />

              <i class="sk sk-line" style="width: 30%; height: 10px; margin-top: 16px" />
              <i class="sk sk-field" />

              <i class="sk sk-field" style="margin-top: 21px" />
            </div>
          </div>

          <div class="col-12 col-xl-8 offset-md-2">
            <i class="sk sk-block" />
          </div>
        </div>
      </div>

      <div class="sk-sheen" />
    </div>

    <!-- [DBG] -->
    <div v-if="dbgVisible && false" class="dbg">
      <div class="dbg-head" @click="open = !open">
        <span class="dbg-now" :class="{ danger: elapsed > 3000 }">
          {{ show ? 'LOADING' : 'DONE' }} {{ (elapsed / 1000).toFixed(1) }}s
        </span>
        <span class="dbg-caret">{{ open ? '&#9660;' : '&#9650;' }}</span>
        <span class="dbg-close" @click.stop="dbgVisible = false">&#10005;</span>
      </div>
      <div v-if="open" class="dbg-body">
        <div v-for="(l, i) in logs" :key="i" :class="['dbg-row', l.kind]">
          <b>+{{ l.at }}</b> {{ l.kind }} &middot; {{ l.tag }}
        </div>
      </div>
    </div>
    <!-- /[DBG] -->
  </div>
</template>

<script>
export default {
  data: () => ({
    elapsed: 0,
    timer: null,
    open: false,
    dbgVisible: true // [DBG]
  }),
  computed: {
    show() {
      return this.$store.state.showFullContentLoader
    },
    logs() {
      return this.$store.state.loaderLogs // [DBG]
    }
  },
  // [DBG]
  watch: {
    show(v) {
      clearInterval(this.timer)
      if (v) {
        this.dbgVisible = true
        this.open = false
        this.elapsed = 0
        const t0 = performance.now()
        this.timer = setInterval(() => {
          this.elapsed = performance.now() - t0
        }, 1000)
      } else {
        const last = this.logs[this.logs.length - 1]
        if (last) this.elapsed = last.at
      }
    }
  },
  // [DBG]
  beforeDestroy() {
    clearInterval(this.timer)
  }
}
</script>

<style lang="scss" scoped>
$sk-base: #e9ecef;
$sk-radius: 4px;

.full-content-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  z-index: 1020;
  overflow: hidden;
}

/* Every shape is a flat, static block: painted once, never invalidated. */
.sk {
  display: block;
  background: $sk-base;
  border-radius: $sk-radius;
}

.sk-line {
  border-radius: $sk-radius;
}

.sk-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  flex: 0 0 60px;
}

.sk-field {
  width: 100%;
  height: 24px;
  margin-top: 5px;
}

.sk-block {
  width: 100%;
  height: 200px;
  margin-top: 16px;
}

.sk-card {
  padding-top: 4px;
}

.sk-id {
  display: flex;
  align-items: center;
}

.sk-id-text {
  flex: 1 1 auto;
  min-width: 0;
  padding-left: 15px;
}

/*
 * One animated element for the whole overlay instead of one per shape.
 * It only ever animates `transform`, so the compositor owns it end to end:
 * no style recalc, no layout, no repaint of anything underneath. Over white
 * background the sheen is invisible; over the grey shapes it reads as a
 * highlight travelling across them.
 */
.sk-sheen {
  position: absolute;
  top: 0;
  left: 0;
  width: 45%;
  height: 100%;
  pointer-events: none;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.75) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  will-change: transform;
  animation: sk-sweep 1.4s linear infinite;
}

@keyframes sk-sweep {
  from {
    transform: translate3d(-100%, 0, 0);
  }
  to {
    transform: translate3d(322%, 0, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sk-sheen {
    display: none;
  }
}

/* [DBG] */
.dbg {
  position: fixed;
  bottom: 8px;
  left: 8px;
  max-width: 92vw;
  background: rgba(0, 0, 0, 0.9);
  color: #ddd;
  font: 12px/1.6 monospace;
  border-radius: 6px;
  z-index: 3000;
}
.dbg-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  cursor: pointer;
}
.dbg-now {
  color: #0f0;
  font-size: 14px;
  font-weight: bold;
}
.dbg-now.danger {
  color: #f44;
}
.dbg-caret {
  color: #888;
}
.dbg-close {
  color: #888;
  margin-left: 4px;
  padding: 0 6px;
}
.dbg-close:hover {
  color: #fff;
}
.dbg-body {
  max-height: 55vh;
  overflow-y: auto;
  padding: 0 10px 8px;
}
.dbg-row b {
  color: #ff0;
  display: inline-block;
  width: 60px;
}
.dbg-row.show { color: #6cf; }
.dbg-row.hide { color: #0f0; }
.dbg-row.dup,
.dbg-row.stray { color: #fa0; }
.dbg-row.warn { color: #f44; font-weight: bold; }
/* /[DBG] */
</style>